import { getAppConfig } from '@bts-web/core-app-config';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';

/**
 * Single place for building asset trade URLs. Respects useLegacyOperationUrls
 * (legacy vs non-legacy shape is an implementation detail and can be removed when the flag is retired).
 * See trade/AccepBackwardsCompatibilityInterceptor/legacy-freeze/README.md
 */

export type AssetTradeUrlContext =
  | { type: 'assetDetails' }
  | { type: 'buy'; trailingSlash?: boolean }
  | { type: 'sell'; trailingSlash?: boolean }
  | { type: 'savings'; trailingSlash?: boolean }
  | { type: 'orderForm'; orderType: 'BUY' | 'SELL'; amount?: string }
  | {
      type: 'acceptOffer';
      variant: 'buy' | 'sell';
      offerId: string;
    }
  | { type: 'createOrderRedirect'; orderType: 'BUY' | 'SELL'; orderId: string }
  | {
      type: 'fiatTransfer';
      variant: 'buy';
      offerId: string;
      transferLink: string;
    };

function legacyBase(assetId: string, variant: 'buy' | 'sell'): string {
  return NEXT_ROUTES.ASSET_DETAILS_LEGACY_ORDER_TYPE.replace(
    '[id]',
    assetId,
  ).replace('[orderOperation]', variant);
}

function maybeSlash(trailingSlash?: boolean): '/' | '' {
  return trailingSlash ? '/' : '';
}

function buildLegacyUrl(
  assetId: string,
  context: AssetTradeUrlContext,
): string {
  switch (context.type) {
    case 'assetDetails':
      return NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId);

    case 'buy':
      return `${legacyBase(assetId, 'buy')}${maybeSlash(context.trailingSlash)}`;

    case 'sell':
      return `${legacyBase(assetId, 'sell')}${maybeSlash(context.trailingSlash)}`;

    case 'savings':
      return `${legacyBase(assetId, 'buy')}/savings${maybeSlash(context.trailingSlash)}`;

    case 'orderForm': {
      const variant = context.orderType.toLowerCase() as 'buy' | 'sell';

      const query = `orderType=${context.orderType}${context.amount ? `&amount=${context.amount}` : ''}`;

      return `${legacyBase(assetId, variant)}/order?${query}`;
    }

    case 'acceptOffer':
      return `${legacyBase(assetId, context.variant)}/order/${context.offerId}`;

    case 'createOrderRedirect': {
      const variant = context.orderType.toLowerCase() as 'buy' | 'sell';

      return `${legacyBase(assetId, variant)}/order/${context.orderId}`;
    }

    default:
      return NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId);
  }
}

function buildCurrentUrl(
  assetId: string,
  context: AssetTradeUrlContext,
): string {
  const base = NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId);

  switch (context.type) {
    case 'assetDetails':
      return base;

    case 'buy':
      return `${base}/buy${maybeSlash(context.trailingSlash)}`;

    case 'sell':
      return `${base}/sell${maybeSlash(context.trailingSlash)}`;

    case 'savings':
      return `${base}/savings${maybeSlash(context.trailingSlash)}`;

    case 'orderForm':
      return `${base}/order?orderType=${context.orderType}${context.amount ? `&amount=${context.amount}` : ''}`;

    case 'acceptOffer':
      return `${base}/${context.variant}/${context.offerId}`;

    case 'fiatTransfer':
      return `${base}/${context.variant}/${context.offerId}?transferLink=${encodeURIComponent(context.transferLink)}`;

    case 'createOrderRedirect':
      return `${base}/order/${context.orderId}`;

    default:
      return base;
  }
}

export function getAssetTradeUrl(
  assetId: string,
  context: AssetTradeUrlContext,
): string {
  if (getAppConfig().useLegacyOperationUrls) {
    return buildLegacyUrl(assetId, context);
  }

  return buildCurrentUrl(assetId, context);
}

/**
 * Legacy-only: build finish trade path (variant/order). Used by finishTradeRedirectBuilder.
 * See trade/AccepBackwardsCompatibilityInterceptor/legacy-freeze/README.md
 */
export function getLegacyFinishTradePath(
  assetId: string,
  variant: 'buy' | 'sell',
): string {
  return `${legacyBase(assetId, variant)}/order`;
}

/**
 * Raiffeisen legacy (savings plan creation) for dev only. Build finish path for savings: /assets/[id]/buy/savings.
 * Used by finishTradeRedirectBuilder when variant is savings and useLegacyOperationUrls is true.
 */
export function getLegacyFinishSavingsPath(assetId: string): string {
  return `${legacyBase(assetId, 'buy')}/savings`;
}
